import { CheckOutlined, CloseOutlined, ExclamationCircleOutlined } from "@ant-design/icons"
import {
  Button,
  Col,
  Input,
  InputNumber,
  message,
  Row,
  Select,
  Space,
  Switch,
  Table,
  Tooltip,
  Typography,
} from "antd"
import { useEffect, useMemo, useState } from "react"
import { useAuthContext } from "../../../hooks/useAuthContext"
import { doc, getDoc } from "firebase/firestore"
import { db } from "../../../firebase/config"
import {
  containsAny,
  startsWithOneOf,
} from "../../../components/Orders/Helpers/ChecksForStringDuplicates"

const { Link, Text } = Typography

// Change 1: Pass isPrivilegedUser into this component
const VendorColumnsComponent = ({
  productData,
  handleDelete,
  fromDrawer,
  productTypeOptions,
  handleVendorProductNameChange,
  handleVendorUnitChange,
  handleVendorCountChange,
  handleVendorPriceChange,
  disabledRowKeys,
  isPrivilegedUser,
  handleProductTypeChange,
}) => {
  const safeDisabledRowKeys = Array.isArray(disabledRowKeys) ? disabledRowKeys : []

  // Change 2: Update the disabled logic for each relevant column
  // A field is disabled if it's an original record AND the user is NOT privileged.
  const isRowDisabledForStandardUser = (record) => {
    return safeDisabledRowKeys.includes(record.key) && !isPrivilegedUser
  }

  return [
    {
      title: "Type",
      dataIndex: "productType",
      width: 150,
      render: (value, record) => (
        <Select
          style={{ width: 150 }}
          placeholder="Select type..."
          value={record.productType || null}
          options={productTypeOptions}
          onChange={(value) => handleProductTypeChange(record.key, value || null)}
          allowClear
          showSearch
          optionFilterProp="label"
        />
      ),
    },
    {
      title: "Product",
      dataIndex: "title",
      width: "40%",
      render: (value, record) => {
        const product = record.product || ""
        const pType = record.productType || ""

        // warn if product already contains/starts with the selected type
        const dupByPrefix = pType && startsWithOneOf(product, [pType])
        const dupAnywhere = pType && containsAny(product, [pType])
        const showWarning = dupByPrefix || dupAnywhere

        return (
          <>
            <Tooltip
              open={showWarning ? undefined : false}
              title="Selected type is already in the product name. Concatenation would duplicate it.">
              <Input
                style={{ width: "100%" }}
                placeholder="Enter product name..."
                value={product}
                status={showWarning ? "warning" : undefined}
                prefix={showWarning ? <ExclamationCircleOutlined /> : undefined}
                onChange={(e) => handleVendorProductNameChange(record.key, e.target.value)}
              />
            </Tooltip>

            {showWarning && (
              <Typography.Text type="warning" style={{ fontSize: 11 }}>
                Product already includes “{pType}”.
              </Typography.Text>
            )}
          </>
        )
      },
    },
    {
      title: "Unit",
      width: "10%",
      dataIndex: "unit",
      render: (value, record) => (
        <Select
          value={record.unit}
          options={[
            { value: "thous.", label: "thous.", count: 1000 },
            { value: "pcs.", label: "pcs.", count: 1 },
          ]}
          // Updated disabled logic here
          disabled={isRowDisabledForStandardUser(record)}
          style={{ width: "100%" }}
          onChange={(value, options) => handleVendorUnitChange(record.key, value)}
        />
      ),
    },
    {
      title: "Count",
      width: "20%",
      dataIndex: "count",
      render: (val, record) => (
        <InputNumber
          // Updated disabled logic here
          disabled={isRowDisabledForStandardUser(record)}
          style={{ width: "100%" }}
          value={record.count}
          onChange={(value) => handleVendorCountChange(record.key, value, record)}
          min={0}
        />
      ),
    },
    {
      title: "Price per unit",
      width: "20%",
      dataIndex: "price",
      render: (value, record) => (
        <InputNumber
          value={record.price}
          onChange={(value) => handleVendorPriceChange(record.key, value, record)}
          style={{ width: "100%" }}
          min={0}
          precision={2}
          // Updated disabled logic here
          disabled={isRowDisabledForStandardUser(record)}
        />
      ),
    },
    {
      title: "Sum",
      width: "10%",
      dataIndex: "sum",
      render: (value, record) => (
        <>
          <InputNumber
            // Updated disabled logic here
            disabled={isRowDisabledForStandardUser(record)}
            precision={2}
            value={value}
          />
        </>
      ),
    },
    {
      width: "10%",
      title: "operation",
      dataIndex: "operation",
      render: (_, record) =>
        productData.length >= 1 ? (
          <Link
            // Updated disabled logic here
            disabled={isRowDisabledForStandardUser(record)}
            onClick={() => handleDelete(record.key)}>
            delete
          </Link>
        ) : null,
    },
  ]
}

export default function ProductsAndServices({
  invoiceData,
  setInvoiceData,
  count,
  setCount,
  availableProducts,
  productsLoading,
  productCount = [],
  setProductCounts,
  setTotalsData,
  fromDrawer,
  docForPrinting,
  disabledInitialRowsCount = 0,
  showWeight,
}) {
  const [productTypeOptions, setProductTypeOptions] = useState([])
  const [loadingTypeOptions, setLoadingTypeOptions] = useState(false)

  useEffect(() => {
    let mounted = true
    const loadTypes = async () => {
      setLoadingTypeOptions(true)
      try {
        const snap = await getDoc(doc(db, "settings", "productTypeList"))
        if (!snap.exists()) throw new Error("Settings document 'productTypeList' not found.")
        const arr = snap.data()?.productTypes ?? []
        if (!Array.isArray(arr) || arr.length === 0) {
          throw new Error("No product types found.")
        }
        if (mounted) {
          setProductTypeOptions(arr.map((s) => ({ value: s, label: s })))
        }
      } catch (err) {
        console.error(err)
        message.error(err.message || "Failed to load product types")
      } finally {
        if (mounted) setLoadingTypeOptions(false)
      }
    }
    loadTypes()
    return () => {
      mounted = false
    }
  }, [])

  //product type update
  const handleProductTypeChange = (key, value) => {
    const updatedProductData = productData.map((item) => {
      if (item.key === key) {
        return {
          ...item,
          productType: value,
        }
      }
      return item
    })

    setInvoiceData({
      ...invoiceData,
      prices: updatedProductData,
    })
  }

  // Memoize productData to stabilize useEffect deps
  const productData = useMemo(() => {
    return Array.isArray(invoiceData?.prices) ? invoiceData.prices : []
  }, [invoiceData?.prices])

  const disabledRowKeys = Array.isArray(productData)
    ? productData.slice(0, disabledInitialRowsCount).map((item) => item.key)
    : []

  const [sumTableData, setSumTableData] = useState([
    {
      key: 1,
      position: "Profit/Discount",
      sum: 0.0,
    },
    {
      key: 2,
      position: "Neto Sum",
      sum: 0.0,
    },
    {
      key: 3,
      position: "21% PVN",
      sum: 0.0,
    },
    {
      key: 4,
      position: "Total Sum",
      sum: 0.0,
    },
  ])

  const [profitDiscount, setProfitDiscount] = useState(0.0)
  const [inputValue, setInputValue] = useState(0)

  // Disables or enables the input for comment
  const [disabledInputs, setDisabledInputs] = useState(
    productData.reduce((obj, item) => {
      obj[item.key] = true
      return obj
    }, {})
  )

  const { admin, semiAdmin, accountant } = useAuthContext()
  const isPrivilegedUser = admin || semiAdmin || accountant

  // Calculation of sums based on product data
  useEffect(() => {
    const calculateSums = () => {
      // Calculate neto sum from all products
      let netoSum = 0
      productData.forEach((item) => {
        const sum = parseFloat(item.sum) || 0
        if (!isNaN(sum)) {
          netoSum += sum
        }
      })

      // Handle case where netoSum is NaN
      if (isNaN(netoSum)) {
        return
      }

      // Apply profit/discount to get final neto sum
      let netoSumAfterDiscount
      if (profitDiscount < 0) {
        netoSumAfterDiscount = parseFloat(netoSum.toFixed(2)) - Math.abs(profitDiscount)
      } else {
        netoSumAfterDiscount = parseFloat(netoSum.toFixed(2)) + profitDiscount
      }

      // Calculate VAT and total
      const pvn = parseFloat((netoSumAfterDiscount * 0.21).toFixed(2))
      const totalSum = parseFloat(pvn.toFixed(2)) + parseFloat(netoSumAfterDiscount.toFixed(2))

      // Update totals data for parent component
      setTotalsData({
        totalProfitDiscount: parseFloat(profitDiscount.toFixed(2)),
        totalNetoSum: parseFloat(netoSumAfterDiscount.toFixed(2)),
        totalVat: parseFloat(pvn.toFixed(2)),
        totalSum: parseFloat(totalSum.toFixed(2)),
      })

      // Update summary table data
      setSumTableData((prevSumTableData) => [
        {
          ...prevSumTableData[0],
          sum: profitDiscount.toFixed(2),
        },
        {
          ...prevSumTableData[1],
          sum: netoSumAfterDiscount.toFixed(2),
        },
        {
          ...prevSumTableData[2],
          sum: pvn.toFixed(2),
        },
        {
          ...prevSumTableData[3],
          sum: totalSum.toFixed(2),
        },
      ])
    }

    calculateSums()
  }, [productData, profitDiscount, setTotalsData])

  // Common handlers
  const handleDelete = (key) => {
    const newData = {
      ...invoiceData,
      prices: invoiceData.prices.filter((item) => item.key !== key),
    }
    setInvoiceData(newData)
  }

  // Handlers for Vendor columns (Custom mode)
  const handleVendorUnitChange = (key, value) => {
    const updatedProductData = productData.map((item) => {
      if (item.key === key) {
        // No unit value conversion needed - "thous." is treated as 1
        const sum = parseFloat((item.count * item.price).toFixed(2))
        const vatRate = 21
        const vat = parseFloat(((sum * vatRate) / 100).toFixed(2))

        return {
          ...item,
          unit: value,
          sum,
          vat,
        }
      }
      return item
    })

    const updatedInvoiceData = {
      ...invoiceData,
      prices: updatedProductData,
    }
    setInvoiceData(updatedInvoiceData)
  }

  const handleVendorCountChange = (key, value, record) => {
    const updatedProductData = productData.map((item) => {
      if (item.key === key) {
        // If unit is "thous.", treat count as thousands: divide by 1000
        const multiplier = record.unit === "thous." ? 1 / 1000 : 1
        const sum = parseFloat((value * record.price * multiplier).toFixed(2))
        // 21% VAT on that sum
        const vat = parseFloat((sum * 0.21).toFixed(2))
        return {
          ...item,
          count: value,
          sum,
          vat,
        }
      }
      return item
    })

    setInvoiceData({
      ...invoiceData,
      prices: updatedProductData,
    })
  }

  const handleVendorPriceChange = (key, value, record) => {
    const updatedProductData = productData.map((item) => {
      if (item.key === key) {
        // If unit is "thous.", treat count as thousands: divide by 1000
        const multiplier = record.unit === "thous." ? 1 / 1000 : 1
        const sum = parseFloat((record.count * value * multiplier).toFixed(2))
        // 21% VAT on that sum
        const vat = parseFloat((sum * 0.21).toFixed(2))
        return {
          ...item,
          price: value,
          sum,
          vat,
        }
      }
      return item
    })

    setInvoiceData({
      ...invoiceData,
      prices: updatedProductData,
    })
  }

  // Handle if the product has a comment
  const handleHasProductComment = (key, value) => {
    const updatedProductData = productData.map((item) => {
      if (item.key === key) {
        return { ...item, hasProductComment: value, productComment: "" }
      }
      return item
    })

    const updatedInvoiceData = {
      ...invoiceData,
      prices: updatedProductData,
    }
    setInvoiceData(updatedInvoiceData)

    // Disables or enables the input for comment
    setDisabledInputs((prevDisabledInputs) => ({
      ...prevDisabledInputs,
      [key]: !value,
    }))
  }

  // Add new product row
  const handleAdd = () => {
    // Create different default values based on mode
    const newData = {
      key: count,
      code: 0,
      product: docForPrinting ? null : "",
      productType: "",
      unit: "thous.", // Keep the default unit as "thous." for both modes
      count: 0,
      price: 0.0,
      sum: 0,
      vat: 0,
      dAccount: 2310,
      dName: "Norēķini ar pircējiem un pasūtītājiem",
      dCurrency: "Eur",
      cAccount: 6110,
      cName: "Ieņēmumi no pamatdarbības produkcijas un pakalpojumu pārdošanas",
      kCurrency: "Eur",
      productComment: "",
      hasProductComment: false,
      hasManualCount: false,
      hasChangableSpace: false,
    }

    const updatedProductData = [...productData, newData]

    const updatedInvoiceData = {
      ...invoiceData,
      prices: updatedProductData,
    }
    setInvoiceData(updatedInvoiceData)

    // Adds the disabled input for comment
    setDisabledInputs((prevDisabledInputs) => ({
      ...prevDisabledInputs,
      [count]: true,
    }))

    setCount(count + 1)
  }

  // Handle profit/discount change
  const handleProfitDiscountChange = (value) => {
    const newProfitDiscount = parseFloat(value)

    let totalNetoSum = 0
    productData.forEach((item) => {
      const sum = parseFloat((item.count * item.price).toFixed(2))
      totalNetoSum += sum
    })

    if (newProfitDiscount < 0 && Math.abs(newProfitDiscount) > totalNetoSum) {
      alert("Absolute value of discount cannot be greater than total neto sum")
    } else {
      setProfitDiscount(newProfitDiscount)
      setInputValue(newProfitDiscount)
    }
  }

  // Add the comment to each product
  const handleAddProductComment = (event, key) => {
    const productComment = event.target.value

    const updatedProductData = productData.map((item) => {
      if (item.key === key) {
        return { ...item, productComment: productComment }
      }
      return item
    })

    const updatedInvoiceData = {
      ...invoiceData,
      prices: updatedProductData,
    }
    setInvoiceData(updatedInvoiceData)
  }

  // Extra row content for comments
  const expandableRowContent = (data) => {
    return (
      <>
        <Row gutter={[15, 15]}>
          {data.hasChangableSpace ? (
            <>
              <Col span={6}>
                Changable space :{" "}
                <Text style={{ color: "red" }} italic>
                  {data.productComment}
                </Text>
              </Col>
            </>
          ) : (
            ""
          )}

          {showWeight && data.weight > 0 && (
            <Col span={6}>
              Weight:{" "}
              <Text style={{ color: "blue" }} italic>
                {data.weight.toFixed(2)} kg
              </Text>
            </Col>
          )}

          <Col span={6}>
            <Space>
              Product has comment :
              <Switch
                disabled={data.hasChangableSpace}
                onChange={(value) => handleHasProductComment(data.key, value)}
                checkedChildren={<CheckOutlined />}
                unCheckedChildren={<CloseOutlined />}
              />
            </Space>
          </Col>

          <Col span={24}>
            {!disabledInputs[data.key] ? (
              <Input
                disabled={disabledInputs[data.key]}
                onChange={(event) => handleAddProductComment(event, data.key)}
                style={{ width: "100%" }}
              />
            ) : (
              ""
            )}
          </Col>
        </Row>
      </>
    )
  }

  // Footer content with summary data and add button
  const footerContent = () => {
    const sumColumns = [
      {
        title: "Position",
        dataIndex: "position",
        width: "55%",
      },
      {
        title: "Sum",
        dataIndex: "sum",
        width: "15%",
        render: (value, record) => (
          <InputNumber
            value={record.key === 1 ? inputValue : value}
            precision={2}
            size="small"
            disabled={record.key !== 1}
            style={{ width: "100%" }}
            onChange={(newValue) => record.key === 1 && handleProfitDiscountChange(newValue)}
          />
        ),
      },
    ]

    return (
      <>
        <Row>
          <Col span={12}>
            <Button onClick={handleAdd}>+</Button>
          </Col>
          <Col span={12}>
            <Table bordered size="small" dataSource={sumTableData} columns={sumColumns} />
          </Col>
        </Row>
      </>
    )
  }

  // Handler for custom product name input in vendor mode
  const handleVendorProductNameChange = (key, value) => {
    const updatedProductData = productData.map((item) => {
      if (item.key === key) {
        return { ...item, product: value }
      }
      return item
    })

    const updatedInvoiceData = {
      ...invoiceData,
      prices: updatedProductData,
    }
    setInvoiceData(updatedInvoiceData)
  }

  // Get the appropriate columns based on docForPrinting state
  const getColumns = () => {
    const vendorColumnProps = {
      productData,
      handleVendorProductNameChange,
      handleVendorUnitChange,
      handleVendorCountChange,
      handleVendorPriceChange,
      handleProductTypeChange,
      handleDelete,
      fromDrawer,
      disabledRowKeys,
      isPrivilegedUser,
      productTypeOptions,
      loadingTypeOptions,
    }
    return VendorColumnsComponent(vendorColumnProps)
  }

  return (
    <div>
      <Table
        size="small"
        expandable={{
          expandedRowRender: (record) => expandableRowContent(record),
        }}
        bordered
        dataSource={productData}
        columns={getColumns()}
        footer={footerContent}
        pagination={false}
      />
    </div>
  )
}
